import { useEffect, useState } from "react";
import { X, Copy, MessageCircle, Send, Twitter, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { getReferralLink, getReferralCount, trackEvent } from "@/lib/analytics";
import { shareLinks } from "@/lib/community";

export default function ReferralShare({ open, onClose, heading = "Share the Knowledge" }) {
  const [count, setCount] = useState(null);
  const link = getReferralLink();
  const share = shareLinks(link, "I just found Apocalypz.org — 330+ free lectures on ancient African wisdom, metaphysics & herbal healing. Awaken with me. 🔮");

  useEffect(() => {
    if (open) getReferralCount().then(setCount);
  }, [open]);

  if (!open) return null;

  const copy = async () => {
    trackEvent("share_click", { channel: "copy_link", kind: "referral" });
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(link);
      else {
        const ta = document.createElement("textarea");
        ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Your invite link is copied — share it!");
    } catch { toast.error("Could not copy link"); }
  };
  const go = (url, channel) => { trackEvent("share_click", { channel, kind: "referral" }); window.open(url, "_blank", "noopener"); };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="referral-modal">
      <div className="w-full max-w-md bg-surface border border-gold/30 shadow-2xl p-7 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-gold"><Sparkles size={18} /><h2 className="font-serif text-2xl text-bone">{heading}</h2></div>
          <button onClick={onClose} data-testid="referral-close" className="text-bone/50 hover:text-gold"><X size={20} /></button>
        </div>

        <div className="text-center bg-black/40 border border-white/10 py-5 rounded-sm" data-testid="referral-count">
          <div className="flex items-center justify-center gap-2 text-gold">
            <Users size={22} />
            <span className="font-serif text-4xl">{count === null ? "…" : count}</span>
          </div>
          <p className="text-bone/55 text-sm mt-1">{count === 1 ? "seeker" : "seekers"} you've awakened so far</p>
        </div>

        <p className="text-bone/60 text-sm">"Each one, reach one." Share your personal link — when someone new enters through it, your count grows. 🌍</p>

        <div className="flex items-center gap-2 bg-black/50 border border-white/10 p-2">
          <input readOnly value={link} data-testid="referral-link" className="flex-1 bg-transparent text-bone/70 text-xs px-2 focus:outline-none" />
          <button onClick={copy} data-testid="referral-copy" className="bg-gold text-black px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-gold2 transition-colors"><Copy size={14} /> Copy</button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => go(share.whatsapp, "whatsapp")} data-testid="referral-whatsapp" className="flex flex-col items-center gap-1.5 border border-white/15 py-3 text-bone/80 hover:border-gold/50 hover:text-gold transition-colors"><MessageCircle size={20} /><span className="text-xs">WhatsApp</span></button>
          <button onClick={() => go(share.telegram, "telegram")} data-testid="referral-telegram" className="flex flex-col items-center gap-1.5 border border-white/15 py-3 text-bone/80 hover:border-gold/50 hover:text-gold transition-colors"><Send size={20} /><span className="text-xs">Telegram</span></button>
          <button onClick={() => go(share.twitter, "twitter")} data-testid="referral-twitter" className="flex flex-col items-center gap-1.5 border border-white/15 py-3 text-bone/80 hover:border-gold/50 hover:text-gold transition-colors"><Twitter size={20} /><span className="text-xs">Post</span></button>
        </div>
      </div>
    </div>
  );
}
